import React, { ChangeEventHandler, ReactNode, useCallback } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Theme,
  Typography
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useTranslation } from "react-i18next";
import { inputLabels, Settings } from "../../../state/settings/settingsReducer";
import { useVideoState } from "../../../state";
import useRoomState from "../../../hooks/useRoomState/useRoomState";
import { typography } from "../../../utils/typography";
import { semanticColors } from "../../../utils/colors";

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    width: "600px",
    minHeight: "400px",
    [theme.breakpoints.down("sm")]: {
      width: "calc(100vw - 32px)"
    },
    "& .inputSelect": {
      width: "calc(100% - 35px)"
    }
  },
  paper: {
    [theme.breakpoints.down("sm")]: {
      margin: "16px"
    },
    borderRadius: "16px !important"
  },
  formControl: {
    display: "block",
    margin: "1.5em 0",
    "&:first-child": {
      margin: "0 0 1.5em 0"
    }
  },
  label: {
    width: "133%"
  }
}));

const withDefault = (val?: string) =>
  typeof val === "undefined" ? "default" : val;

export default function ConnectionOptionsDialog({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { settings, dispatchSetting } = useVideoState();
  const roomState = useRoomState();
  const isDisabled = roomState !== "disconnected";

  const handleChange = useCallback(
    (e: SelectChangeEvent<string>) => {
      dispatchSetting({
        name: e.target.name as keyof Settings,
        value: e.target.value as string
      });
    },
    [dispatchSetting]
  );

  const handleNumberChange = useCallback(
    (e: any) => {
      if (!/[^\d]/.test(e.target.value as string)) handleChange(e);
    },
    [handleChange]
  );

  return (
    <Dialog open={open} onClose={onClose} classes={{ paper: classes.paper }}>
      <DialogTitle
        sx={{ ...typography.heading.s, color: semanticColors.text.primary }}
      >
        {t("settings.connection")}
      </DialogTitle>
      <Divider />
      <DialogContent className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography
              hidden={!isDisabled}
              sx={{
                ...typography.body.m,
                color: semanticColors.text.secondary
              }}
            >
              {t("settings.connectionDisabledHint")}
            </Typography>
          </Grid>

          <Grid item sm={6} xs={12}>
            <FormControl className={classes.formControl}>
              <InputLabel id={inputLabels.dominantSpeakerPriority}>
                {t("settings.dominantSpeakerPriority")}:
              </InputLabel>
              <Select
                fullWidth
                disabled={isDisabled}
                name={inputLabels.dominantSpeakerPriority}
                label={inputLabels.dominantSpeakerPriority}
                value={withDefault(settings.dominantSpeakerPriority)}
                onChange={handleChange}
              >
                <MenuItem value="low">{t("settings.low")}</MenuItem>
                <MenuItem value="standard">{t("settings.standard")}</MenuItem>
                <MenuItem value="high">{t("settings.high")}</MenuItem>
                <MenuItem value="default">
                  {t("settings.serverDefault")}
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl className={classes.formControl}>
              <InputLabel id={inputLabels.trackSwitchOffMode}>
                {t("settings.trackSwitchOffMode")}:
              </InputLabel>
              <Select
                fullWidth
                disabled={isDisabled}
                name={inputLabels.trackSwitchOffMode}
                label={inputLabels.trackSwitchOffMode}
                value={withDefault(settings.trackSwitchOffMode)}
                onChange={handleChange}
              >
                <MenuItem value="predicted">{t("settings.predicted")}</MenuItem>
                <MenuItem value="detected">{t("settings.detected")}</MenuItem>
                <MenuItem value="disabled">{t("settings.disabled")}</MenuItem>
                <MenuItem value="default">
                  {t("settings.serverDefault")}
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl className={classes.formControl}>
              <InputLabel id={inputLabels.bandwidthProfileMode}>
                {t("settings.bandwidthMode")}:
              </InputLabel>
              <Select
                fullWidth
                disabled={isDisabled}
                name={inputLabels.bandwidthProfileMode}
                label={inputLabels.bandwidthProfileMode}
                value={withDefault(settings.bandwidthProfileMode)}
                onChange={handleChange}
              >
                <MenuItem value="grid">{t("settings.grid")}</MenuItem>
                <MenuItem value="collaboration">
                  {t("settings.collaboration")}
                </MenuItem>
                <MenuItem value="presentation">
                  {t("settings.presentation")}
                </MenuItem>
                <MenuItem value="default">
                  {t("settings.serverDefault")}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item sm={6} xs={12}>
            <FormControl className={classes.formControl}>
              <InputLabel id={inputLabels.clientTrackSwitchOffControl}>
                {t("settings.clientTrackSwitchOffControl")}:
              </InputLabel>
              <Select
                fullWidth
                disabled={isDisabled}
                name={inputLabels.clientTrackSwitchOffControl}
                label={inputLabels.clientTrackSwitchOffControl}
                value={withDefault(settings.clientTrackSwitchOffControl)}
                onChange={handleChange}
              >
                <MenuItem value="auto">{t("settings.auto")}</MenuItem>
                <MenuItem value="manual">{t("settings.manual")}</MenuItem>
                <MenuItem value="default">{t("settings.default")}</MenuItem>
              </Select>
            </FormControl>

            <FormControl className={classes.formControl}>
              <InputLabel id={inputLabels.contentPreferencesMode}>
                {t("settings.contentPreferencesMode")}:
              </InputLabel>
              <Select
                fullWidth
                disabled={isDisabled}
                name={inputLabels.contentPreferencesMode}
                label={inputLabels.contentPreferencesMode}
                value={withDefault(settings.contentPreferencesMode)}
                onChange={handleChange}
              >
                <MenuItem value="auto">{t("settings.auto")}</MenuItem>
                <MenuItem value="manual">{t("settings.manual")}</MenuItem>
                <MenuItem value="default">{t("settings.default")}</MenuItem>
              </Select>
            </FormControl>

            <FormControl className={classes.formControl}>
              <TextField
                disabled={isDisabled}
                fullWidth
                id={inputLabels.maxAudioBitrate}
                label={t("settings.maxAudioBitrate")}
                placeholder={t("settings.noLimit")}
                name={inputLabels.maxAudioBitrate}
                value={withDefault(settings.maxAudioBitrate)}
                onChange={handleNumberChange}
              />
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Box
          component="button"
          onClick={onClose}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: semanticColors.gradient.surface,
            borderRadius: "40px",
            height: "44px",
            border: "none",
            cursor: "pointer",
            px: "24px"
          }}
        >
          <Typography
            sx={{
              ...typography.body.lBold,
              color: semanticColors.text.inverse
            }}
          >
            {t("settings.done")}
          </Typography>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
